// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from '@/integrations/supabase/client';
import { Users, Phone, Mail, Calendar, MapPin, Trophy } from 'lucide-react';
import { toast } from 'sonner';

interface TeamProspect {
  id: string;
  team_name: string;
  school_organization: string;
  sport: string;
  competitive_level: string;
  coach_name: string;
  coach_email: string;
  coach_phone: string;
  team_size: number;
  geographic_region: string;
  pipeline_stage: string;
  priority_level: string;
  last_contact_date: string;
  notes: string;
  tags: string[];
}

const pipelineStages = [
  { key: 'target', label: 'Target', color: 'bg-gray-500' },
  { key: 'initial_contact', label: 'Initial Contact', color: 'bg-blue-500' },
  { key: 'interest_shown', label: 'Interest Shown', color: 'bg-yellow-500' },
  { key: 'registration_started', label: 'Registration Started', color: 'bg-orange-500' },
  { key: 'registered', label: 'Registered', color: 'bg-green-500' },
  { key: 'active_participant', label: 'Active Participant', color: 'bg-purple-500' },
  { key: 'renewal', label: 'Renewal', color: 'bg-pink-500' }
];

const TeamRecruitmentPipeline: React.FC = () => {
  const [prospects, setProspects] = useState<TeamProspect[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      const { data, error } = await supabase
        .from('team_prospects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProspects(data || []);
    } catch (error) {
      console.error('Error fetching prospects:', error);
      toast.error('Failed to load team prospects');
    } finally {
      setLoading(false);
    }
  };

  const moveToStage = async (prospectId: string, newStage: string) => {
    try {
      const { error } = await supabase
        .from('team_prospects')
        .update({ pipeline_stage: newStage })
        .eq('id', prospectId);

      if (error) throw error;
      
      setProspects(prev => 
        prev.map(p => p.id === prospectId ? { ...p, pipeline_stage: newStage } : p)
      );
      
      toast.success('Team moved to new stage');
    } catch (error) {
      console.error('Error updating prospect stage:', error);
      toast.error('Failed to update team stage');
    }
  };

  const getStageColor = (stage: string) => {
    const stageInfo = pipelineStages.find(s => s.key === stage);
    return stageInfo?.color || 'bg-gray-500';
  };

  const getStageProspects = (stage: string) => {
    return prospects.filter(p => p.pipeline_stage === stage);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Team Recruitment Pipeline</CardTitle>
          <CardDescription>
            Track teams through the recruitment process from initial contact to active participation
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
        {pipelineStages.map((stage) => {
          const stageProspects = getStageProspects(stage.key);
          
          return (
            <Card key={stage.key} className="min-h-[600px]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">
                    {stage.label}
                  </CardTitle>
                  <Badge variant="secondary" className={`${stage.color} text-white`}>
                    {stageProspects.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-3">
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {stageProspects.map((prospect) => (
                      <Card key={prospect.id} className="p-3 cursor-pointer hover:shadow-md transition-shadow">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <h4 className="font-semibold text-sm truncate">
                              {prospect.team_name}
                            </h4>
                            <Badge variant={getPriorityColor(prospect.priority_level)} className="text-xs">
                              {prospect.priority_level}
                            </Badge>
                          </div>
                          
                          <div className="text-xs text-muted-foreground">
                            {prospect.school_organization}
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Trophy className="h-3 w-3" />
                            <span>{prospect.sport}</span>
                            <span>•</span>
                            <span>{prospect.competitive_level}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Users className="h-3 w-3" />
                            <span>{prospect.coach_name}</span>
                          </div>
                          
                          {prospect.team_size && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Users className="h-3 w-3" />
                              <span>{prospect.team_size} players</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span>{prospect.geographic_region}</span>
                          </div>
                          
                          {prospect.last_contact_date && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>Last contact: {new Date(prospect.last_contact_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          
                          {prospect.tags && prospect.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {prospect.tags.slice(0, 2).map((tag, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {prospect.tags.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{prospect.tags.length - 2}
                                </Badge>
                              )}
                            </div>
                          )}
                          
                          <div className="flex gap-1 pt-2">
                            {stage.key !== pipelineStages[pipelineStages.length - 1].key && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs h-6"
                                onClick={() => {
                                  const currentIndex = pipelineStages.findIndex(s => s.key === stage.key);
                                  if (currentIndex < pipelineStages.length - 1) {
                                    moveToStage(prospect.id, pipelineStages[currentIndex + 1].key);
                                  }
                                }}
                              >
                                →
                              </Button>
                            )}
                            
                            <div className="flex gap-1">
                              {prospect.coach_email && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => window.open(`mailto:${prospect.coach_email}`)}
                                >
                                  <Mail className="h-3 w-3" />
                                </Button>
                              )}
                              
                              {prospect.coach_phone && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => window.open(`tel:${prospect.coach_phone}`)}
                                >
                                  <Phone className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export { TeamRecruitmentPipeline };